import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Button,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─── typewriter hook ───────────────────────────────────────────────────────────

function useTypewriter(text, { speed = 18, delay = 0, active = false } = {}) {
  const [displayed, setDisplayed] = useState('');
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!active || !text) return;

    // Si el navegador está traduciendo la página (Google Translate), no animamos:
    // mostramos el texto completo de una vez. Animar carácter por carácter muta el
    // nodo de texto cientos de veces y choca con los <font> que inyecta el traductor,
    // lo que puede tumbar la página. La red de seguridad de index.js evita el crash;
    // esto además evita que la reseña se vea caótica mientras se traduce.
    const html = typeof document !== 'undefined' ? document.documentElement : null;
    const pageTranslated =
      html && (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl'));
    if (pageTranslated) {
      setDisplayed(text);
      setDone(true);
      return;
    }

    setDisplayed('');
    setDone(false);
    let i = 0;
    const timeout = setTimeout(() => {
      const interval = setInterval(() => {
        i++;
        setDisplayed(text.slice(0, i));
        if (i >= text.length) {
          clearInterval(interval);
          setDone(true);
        }
      }, speed);
      return () => clearInterval(interval);
    }, delay);
    return () => clearTimeout(timeout);
  }, [active, text, speed, delay]);

  return { displayed, done };
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function parseTexto(texto = '') {
  const antesMatch = texto.match(/Antes:\s*(.+?)(?=\s*Ahora:|$)/si);
  const ahoraMatch = texto.match(/Ahora:\s*(.+)/si);
  return {
    antes: antesMatch ? antesMatch[1].trim() : texto,
    ahora: ahoraMatch ? ahoraMatch[1].trim() : '',
  };
}

function getInitials(nombre = '') {
  return nombre
    .split(' ')
    .map(w => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

function mapReview(r, i) {
  const { antes, ahora } = parseTexto(r.texto);
  return {
    id: i,
    nombre: r.nombre,
    initials: getInitials(r.nombre),
    rating: r.rating || 5,
    plan: r.plan === 'Head Coach' ? 'Coach: Agustín Urzúa' : r.plan === 'Team Coach' ? 'Team Coach AU' : (r.plan || ''),
    semanas: r.tiempo ? `${r.tiempo} semana${r.tiempo === '1' ? '' : 's'}` : null,
    antes,
    ahora,
    fecha: r.fecha || '',
  };
}

// ─── Stars ────────────────────────────────────────────────────────────────────

const Stars = ({ n = 5, size = 14, showNumber = false }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: '2px' }}>
    {Array.from({ length: 5 }).map((_, i) => {
      const pct = Math.min(1, Math.max(0, n - i));
      return (
        <Box key={i} sx={{ position: 'relative', fontSize: size, lineHeight: 1 }}>
          <Box component="span" sx={{ color: '#CBD5E1' }}>★</Box>
          {pct > 0 && (
            <Box
              component="span"
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                overflow: 'hidden',
                width: `${pct * 100}%`,
                color: '#1A7FFF',
                whiteSpace: 'nowrap',
                pointerEvents: 'none',
              }}
            >
              ★
            </Box>
          )}
        </Box>
      );
    })}
    {showNumber && (
      <Box
        component="span"
        sx={{ fontSize: size * 0.85, fontWeight: 700, color: '#1A7FFF', fontFamily: 'Kanit, sans-serif', ml: '3px', lineHeight: 1 }}
      >
        {Number.isInteger(n) ? n : n}
      </Box>
    )}
  </Box>
);

// ─── ReviewCard ───────────────────────────────────────────────────────────────

const ReviewCard = ({ review }) => {
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = cardRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); observer.disconnect(); } },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const { displayed: antesText } = useTypewriter(review.antes, { speed: 4, delay: 150, active: visible });
  const { displayed: ahoraText } = useTypewriter(review.ahora, { speed: 4, delay: 150, active: visible });

  return (
    <Card
      ref={cardRef}
      elevation={0}
      sx={{
        border: '1px solid #E2E8F0',
        borderTop: '3px solid #1A7FFF',
        borderRadius: '12px',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#fff',
        opacity: visible ? 1 : 0.35,
        filter: visible ? 'none' : 'blur(2px) grayscale(0.4)',
        transform: visible ? 'scale(1)' : 'scale(0.97)',
        transition: 'opacity 0.5s ease, filter 0.5s ease, transform 0.5s ease',
      }}
    >
      <CardContent sx={{ p: 2.5, flex: 1, display: 'flex', flexDirection: 'column' }}>

        {/* Header — nombre + semanas */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.5, pb: 1.5, borderBottom: '1px solid #F1F5F9' }}>
          <Avatar sx={{ width: 34, height: 34, bgcolor: '#EBF3FF', color: '#1A7FFF', fontSize: 13, fontWeight: 700, fontFamily: 'Kanit, sans-serif' }}>
            {review.initials}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, lineHeight: 1.2, fontFamily: 'Kanit, sans-serif', color: '#0F1117' }}>
              {review.nombre}
            </Typography>
            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: 'Kanit, sans-serif', mt: 0.2 }}>
              {[review.semanas, review.plan].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <Stars n={review.rating} size={13} showNumber />
        </Box>

        {/* Antes */}
        {review.antes && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.4, color: '#94A3B8', textTransform: 'uppercase', mb: 0.5, fontFamily: 'Kanit, sans-serif' }}>
              Antes
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.55, color: '#64748B', fontStyle: 'italic', fontFamily: 'Kanit, sans-serif', minHeight: '3em' }}>
              "{antesText}"
            </Typography>
          </Box>
        )}

        {/* Separador → */}
        {review.antes && review.ahora && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#E2E8F0' }} />
            <Typography sx={{ fontSize: 12, color: '#1A7FFF', fontWeight: 700 }}>→</Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: '#E2E8F0' }} />
          </Box>
        )}

        {/* Ahora */}
        {review.ahora && (
          <Box sx={{ mb: 2, flex: 1 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.4, color: '#1A7FFF', textTransform: 'uppercase', mb: 0.5, fontFamily: 'Kanit, sans-serif' }}>
              Ahora
            </Typography>
            <Typography sx={{ fontSize: '0.875rem', lineHeight: 1.55, color: '#0F1117', fontWeight: 500, fontFamily: 'Kanit, sans-serif', minHeight: '3em' }}>
              "{ahoraText}"
            </Typography>
          </Box>
        )}

      </CardContent>
    </Card>
  );
};

// ─── ReviewCard modal (layout horizontal) ─────────────────────────────────────

const ReviewRow = ({ review }) => (
  <Box sx={{ py: 2.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.25 }}>
      <Avatar
        sx={{
          width: 40,
          height: 40,
          bgcolor: '#EBF3FF',
          color: '#1A7FFF',
          fontSize: 14,
          fontWeight: 700,
          fontFamily: 'Kanit, sans-serif',
        }}
      >
        {review.initials}
      </Avatar>
      <Box>
        <Typography sx={{ fontSize: 14, fontWeight: 600, fontFamily: 'Kanit, sans-serif' }}>
          {review.nombre}
        </Typography>
        <Typography sx={{ fontSize: 11.5, color: '#94A3B8', fontFamily: 'Kanit, sans-serif' }}>
          {[review.semanas, review.plan].filter(Boolean).join(' · ')}
        </Typography>
      </Box>
      <Box sx={{ ml: 'auto' }}>
        <Stars n={review.rating} size={12} showNumber />
      </Box>
    </Box>

    {review.antes && (
      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: '#94A3B8', textTransform: 'uppercase', mb: 0.3, fontFamily: 'Kanit, sans-serif' }}>
          Antes
        </Typography>
        <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: '#64748B', fontStyle: 'italic', fontFamily: 'Kanit, sans-serif' }}>
          "{review.antes}"
        </Typography>
      </Box>
    )}
    {review.ahora && (
      <Box>
        <Typography sx={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, color: '#1A7FFF', textTransform: 'uppercase', mb: 0.3, fontFamily: 'Kanit, sans-serif' }}>
          Ahora
        </Typography>
        <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, color: '#0F1117', fontWeight: 500, fontFamily: 'Kanit, sans-serif' }}>
          "{review.ahora}"
        </Typography>
      </Box>
    )}
  </Box>
);

// ─── ReviewsSection ───────────────────────────────────────────────────────────

const ReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    fetch(`${API_URL}/api/reviews`)
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data) && data.length > 0) {
          const shuffled = [...data];
          for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
          }
          setReviews(shuffled.map(mapReview));
        }
      })
      .catch(() => {});
  }, []);

  if (reviews.length === 0) return null;

  const avg = (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(2).replace(/\.?0+$/, '');

  return (
    <Box id="resenas" sx={{ bgcolor: '#0F1117', pt: { xs: 6, md: 10 }, pb: 0 }}>
      <Container maxWidth="lg">

        {/* Header */}
        <Box sx={{ mb: { xs: 4, md: 6 } }}>
          <Typography
            sx={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: 2.5,
              color: '#1A7FFF',
              textTransform: 'uppercase',
              mb: 2,
              fontFamily: 'Kanit, sans-serif',
            }}
          >
            Resultados reales
          </Typography>
          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '2rem', md: '2.6rem' },
              lineHeight: 1.1,
              color: '#fff',
              fontFamily: 'Kanit, sans-serif',
              mb: 4,
            }}
          >
            Lo que dicen los clientes.
          </Typography>

          {/* Rating promedio */}
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 2,
              bgcolor: 'rgba(255,255,255,0.06)',
              border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: '100px',
              px: 3,
              py: 1.25,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: 32, md: 38 },
                fontWeight: 700,
                lineHeight: 1,
                color: '#fff',
                fontFamily: 'Kanit, sans-serif',
              }}
            >
              {avg}
            </Typography>
            <Box>
              <Stars n={5} size={16} />
              {/* {reviews.length} reseñas — oculto por ahora */}
            </Box>
          </Box>
          </Box>
        </Box>

      </Container>

        {/* Scroll horizontal — contenido al ancho del VSL */}
        <Container maxWidth="lg" sx={{ overflow: 'hidden', px: { xs: 2, md: 3 }, bgcolor: '#0F1117' }}>
        <Box
          ref={scrollRef}
          sx={{
            display: 'flex',
            gap: 2,
            overflowX: 'auto',
            pb: 1,
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          {reviews.map((r) => (
            <Box
              key={r.id}
              sx={{ flexShrink: 0, width: { xs: 'calc(100vw - 64px)', sm: 300 } }}
            >
              <ReviewCard review={r} />
            </Box>
          ))}
        </Box>
        </Container>

        {/* CTA ver todas */}
        {reviews.length > 6 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 0 }}>
            <Button
              variant="outlined"
              onClick={() => setModalOpen(true)}
              sx={{
                borderColor: 'rgba(255,255,255,0.2)',
                color: '#fff',
                px: 5, py: 1.75,
                fontSize: '0.95rem',
                fontWeight: 600,
                fontFamily: 'Kanit, sans-serif',
                borderRadius: 2,
                textTransform: 'none',
                minWidth: 220,
                '&:hover': {
                  borderColor: '#1A7FFF',
                  bgcolor: 'rgba(26,127,255,0.08)',
                },
              }}
            >
              Ver todas las reseñas
            </Button>
          </Box>
        )}

      {/* Modal */}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: { xs: '20px 20px 0 0', sm: 2 },
            m: { xs: 0, sm: 2 },
            alignSelf: { xs: 'flex-end', sm: 'center' },
            height: { xs: '92vh', sm: 'auto' },
            maxHeight: { sm: '90vh' },
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            py: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid #F1F5F9',
          }}
        >
          <Typography sx={{ fontSize: 17, fontWeight: 700, fontFamily: 'Kanit, sans-serif' }}>
            {avg} ★ · {reviews.length} reseñas
          </Typography>
          <IconButton onClick={() => setModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ p: 3 }}>
          {reviews.map((r, idx) => (
            <React.Fragment key={r.id}>
              <ReviewRow review={r} />
              {idx < reviews.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ReviewsSection;
